/**
 * useProjectNavigation
 * Hook for project-scoped URL navigation with workspace awareness
 */

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject, getProjectShortId } from '../contexts/ProjectContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

interface UseProjectNavigationReturn {
  buildUrl: (path: string) => string;
  navigateToProject: (path: string, options?: { replace?: boolean; state?: unknown }) => void;
  navigateToOtherProject: (projectId: string, path: string, options?: { replace?: boolean; state?: unknown }) => void;
  getCurrentPathSegment: () => string;
  isProjectScoped: boolean;
  urlShortId: string | null;
}

export function useProjectNavigation(): UseProjectNavigationReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const { shortId, buildProjectUrl } = useProject();
  const { wsSlug } = useWorkspace();

  // Extract shortId from current URL (handles /w/:ws/product/:shortId and legacy /pr/:shortId)
  const urlMatch = location.pathname.match(/(?:^\/w\/[^/]+)?\/(?:product|pr?)\/([^/]+)/);
  const urlShortId = urlMatch ? urlMatch[1] : null;
  const isProjectScoped = urlShortId !== null;

  const buildUrl = useCallback((path: string): string => {
    return buildProjectUrl(path);
  }, [buildProjectUrl]);

  const navigateToProject = useCallback(
    (path: string, options?: { replace?: boolean; state?: unknown }) => {
      const url = buildProjectUrl(path);
      navigate(url, options);
    },
    [buildProjectUrl, navigate]
  );

  // Navigate to a different project's URL — hard navigation to clear stale state
  const navigateToOtherProject = useCallback(
    (
      projectId: string,
      path: string,
      _options?: { replace?: boolean; state?: unknown }
    ) => {
      const targetShortId = projectId.includes('_')
        ? getProjectShortId(projectId)
        : projectId;

      const cleanPath = path.replace(/^\/+/, '');
      const url = wsSlug
        ? `/w/${wsSlug}/product/${targetShortId}/${cleanPath}`
        : `/product/${targetShortId}/${cleanPath}`;
      window.location.href = url;
    },
    [wsSlug]
  );

  const getCurrentPathSegment = useCallback((): string => {
    if (isProjectScoped && urlShortId) {
      // Remove /w/:ws/product/:shortId/ or /pr/:shortId/ prefix
      const withoutPrefix = location.pathname
        .replace(/^\/w\/[^/]+\/product\/[^/]+\//, '')
        .replace(/^\/w\/[^/]+\/pr\/[^/]+\//, '')
        .replace(/^\/pr\/[^/]+\//, '')
        .replace(/^\/p\/[^/]+\//, '');
      return withoutPrefix || '';
    }
    return location.pathname.replace(/^\/+/, '');
  }, [location.pathname, isProjectScoped, urlShortId]);

  return {
    buildUrl,
    navigateToProject,
    navigateToOtherProject,
    getCurrentPathSegment,
    isProjectScoped,
    urlShortId,
  };
}

export default useProjectNavigation;
